import { useState } from 'react';
import { Table, Button, Form, Image, Row, Col, Card } from 'react-bootstrap';
import { toast } from "react-toastify";
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import {
  useAdminListPartCategoriesQuery,
  useAdminCreatePartCategoryMutation,
  useAdminUpdatePartCategoryMutation,
  useAdminDeletePartCategoryMutation,
  useUploadImageMutation,
  useDeleteImageMutation, 
} from '../../slices/productsApiSlice';
import BackButton from "../../Components/BackButton";

export default function PartCategoryAdminScreen() {
  const { data: items = [], isLoading, error } = useAdminListPartCategoriesQuery();
  
  const [createCat, { isLoading: creating }] = useAdminCreatePartCategoryMutation();
  const [updateCat, { isLoading: updating }] = useAdminUpdatePartCategoryMutation();
  const [deleteCat, { isLoading: deleting }] = useAdminDeletePartCategoryMutation();
  
  // Upload and Delete Hooks
  const [uploadImage, { isLoading: uploading }] = useUploadImageMutation();
  const [deleteImage] =   useDeleteImageMutation();

  const [form, setForm] = useState({ key: '', name: '', image: '', sort: 0, active: true, browseMode: 'vehicle' });

  // --- HELPER: Upload file and return URL ---
  const uploadFileHandler = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await uploadImage(formData).unwrap();
    return res.imageUrl; // Ensure your backend returns { imageUrl: '...' }
  };

  // --- CREATE HANDLER (Text Only) ---
  const onCreate = async () => {
    try {
      await createCat(form).unwrap();
      setForm({ key: '', name: '', image: '', sort: 0, active: true, browseMode: 'vehicle' });
      toast.success('Category created');
    } catch (err) {
      toast.error(err?.data?.message || 'Create failed');
    }
  };

  // --- EDIT UPLOAD HANDLER (Replaces & Deletes Old S3 Image) ---
  const onEditUpload = async (e, cat) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.warn('Please choose a PNG, JPG, or WebP image');
      return;
    }

    try {
      // 1. Upload New Image
      const newUrl = await uploadFileHandler(file);

      // 2. Update Database
      await updateCat({ id: cat._id, image: newUrl }).unwrap();

      // 3. Delete Old Image from S3 (Cleanup)
      if (cat.image) {
        await deleteImage({ image: cat.image });
      }

      toast.success('Image updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || 'Upload failed');
    } finally {
      e.target.value = ''; // Reset input
    }
  };

  // --- DELETE HANDLER (Deletes S3 Image & Database Record) ---
  const handleDelete = async (cat) => {
    if (!window.confirm('Delete this category permanently?')) return;

    try {
      // 1. Delete image from S3 if it exists
      if (cat.image) {
        await deleteImage({ image: cat.image }).unwrap();
      }

      // 2. Delete category from DB
      await deleteCat(cat._id).unwrap();
      toast.success('Category deleted successfully');
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed');
    }
  };

  return (
    <>
    <BackButton />
      <h1>Part Categories</h1>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label>Key (slug)</Form.Label>
              <Form.Control
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="brake-pads"
              />
            </Col>
            <Col md={3}>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Brake Pads"
              />
            </Col>
            <Col md={3}>
              <Form.Label>Image</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="/images/parts/brake-pads.png"
                />
              </div>
            </Col>
            <Col md={2}>
              <Form.Label>Browse flow</Form.Label>
              <Form.Select value={form.browseMode} onChange={(e) => setForm({ ...form, browseMode: e.target.value })}>
                <option value="vehicle">Vehicle fitment</option>
                <option value="catalog">Direct catalog</option>
              </Form.Select>
            </Col>
            <Col md={1}>
              <Form.Label>Sort</Form.Label>
              <Form.Control
                type="number"
                value={form.sort}
                onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })}
              />
            </Col>
            <Col md={1} className="d-flex">
              <Button onClick={onCreate} disabled={!form.key || !form.name || creating}>Add</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading && <Loader />}
      {error && <Message variant="danger">{error?.data?.message || error.error}</Message>}

      {!isLoading && !error && (
        <Table striped bordered hover responsive className="table-sm align-middle">
          <thead>
            <tr>
              <th style={{width:96}}>Image</th>
              <th>Name</th>
              <th>Key</th>
              <th className="text-center">Active</th>
              <th>Browse flow</th>
              <th style={{width:110}}>Sort</th>
              <th className="text-end"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it._id}>
                <td>
                    {it.image ? (
                        <Image 
                            src={it.image} 
                            alt={it.name} 
                            style={{ maxHeight: 36 }} 
                            onError={(e) => e.target.style.display = 'none'} 
                        />
                    ) : null}
                </td>
                <td>
                  <Form.Control defaultValue={it.name} onBlur={(e) => updateCat({ id: it._id, name: e.target.value })}/>
                </td>
                <td>
                  <Form.Control defaultValue={it.key} onBlur={(e) => updateCat({ id: it._id, key: e.target.value })}/>
                </td>
                <td className="text-center">
                  <Form.Check
                    type="switch"
                    defaultChecked={it.active}
                    onChange={(e) => updateCat({ id: it._id, active: e.target.checked })}
                  />
                </td>
                <td>
                  <Form.Select defaultValue={it.browseMode || 'vehicle'} onChange={(e) => updateCat({ id: it._id, browseMode: e.target.value })}>
                    <option value="vehicle">Vehicle fitment</option>
                    <option value="catalog">Direct catalog</option>
                  </Form.Select>
                </td>
                <td>
                  <Form.Control
                    type="number"
                    defaultValue={it.sort || 0}
                    onBlur={(e) => updateCat({ id: it._id, sort: Number(e.target.value) })}
                  />
                </td>
                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    {/* Fixed Update Upload (Includes S3 Delete logic) */}
                    <Form.Control
                      type="file"
                      onChange={(e) => onEditUpload(e, it)}
                    />
                    {/* Fixed Delete Button (Includes S3 Delete logic) */}
                    <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => handleDelete(it)} 
                        disabled={deleting}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {(creating || updating || deleting || uploading) && <Loader />}
    </>
  );
}
